import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const PushTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reginaHour, setReginaHour] = useState(null);

  useEffect(() => {
    // Regina = UTC-6, no DST
    const updateTime = () => {
      const now = new Date();
      const utcH = now.getUTCHours();
      const utcM = now.getUTCMinutes();
      const regH = (utcH - 6 + 24) % 24;
      setReginaHour({ hour: regH, minute: utcM, inWindow: regH >= 8 && regH < 20 });
    };
    updateTime();
    const t = setInterval(updateTime, 60000);
    return () => clearInterval(t);
  }, []);
  // Daily push
  const [sending, setSending] = useState(false);
  // Emergency push
  const [emergencyTitle, setEmergencyTitle] = useState('');
  const [emergencyMsg, setEmergencyMsg] = useState('');
  const [emergencyUrl, setEmergencyUrl] = useState('/');
  const [pushSecret, setPushSecret] = useState('');
  const [emergencySending, setEmergencySending] = useState(false);
  // Set secret
  const [newSecret, setNewSecret] = useState('');
  const [confirmSecret, setConfirmSecret] = useState('');
  const [currentSecret, setCurrentSecret] = useState('');
  const [savingSecret, setSavingSecret] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/push/stats');
      setStats(res.data);
    } catch {}
  };

  useEffect(() => { fetchStats(); }, []);

  const sendDaily = async () => {
    if (!window.confirm('Send daily contextual pushes to all eligible subscribers?')) return;
    setSending(true);
    try {
      const res = await api.post('/admin/push/send-daily');
      alert(`Sent: ${res.data.sent} | Skipped (already notified): ${res.data.skipped} | Failed: ${res.data.failed}`);
      fetchStats();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const sendEmergency = async (e) => {
    e.preventDefault();
    if (!pushSecret) { alert('Push secret required'); return; }
    if (!emergencyTitle || !emergencyMsg) { alert('Title and message required'); return; }
    if (!window.confirm(`Send emergency push to ALL subscribers?\n\n"${emergencyTitle}"\n${emergencyMsg}`)) return;
    setEmergencySending(true);
    try {
      const res = await api.post('/admin/push/emergency', {
        push_secret: pushSecret,
        title: emergencyTitle,
        message: emergencyMsg,
        url: emergencyUrl,
      });
      alert(`Emergency push sent to ${res.data.sent} subscribers.`);
      setEmergencyTitle('');
      setEmergencyMsg('');
      setPushSecret('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed — check push secret');
    } finally { setEmergencySending(false); }
  };

  const saveSecret = async (e) => {
    e.preventDefault();
    if (newSecret !== confirmSecret) { alert('Secrets do not match'); return; }
    if (newSecret.length < 8) { alert('At least 8 characters required'); return; }
    if (stats?.push_secret_configured && !currentSecret) {
      alert('Enter your current push secret to change it');
      return;
    }
    setSavingSecret(true);
    try {
      await api.post('/admin/push/set-secret', {
        secret: newSecret,
        current_secret: currentSecret || undefined
      });
      alert('Push secret saved. Keep it somewhere safe — it cannot be recovered.');
      setNewSecret('');
      setConfirmSecret('');
      setCurrentSecret('');
      fetchStats();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed — check your current secret');
    } finally { setSavingSecret(false); }
  };

  return (
    <div className="space-y-8 font-mono">
      <div>
        <h2 className="text-xl font-bold text-white tracking-wider uppercase">Push Notifications</h2>
        <p className="text-gray-600 text-xs mt-1">Web push — works on desktop and Android. iOS requires home screen install.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
          <p className="text-3xl font-black text-[#C5A52A]">{stats?.subscribers ?? '—'}</p>
          <p className="text-xs text-gray-500 mt-1">Push subscribers</p>
        </div>
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
          <p className={`text-sm font-bold mt-1 ${stats?.push_secret_configured ? 'text-green-400' : 'text-red-400'}`}>
            {stats?.push_secret_configured ? '✓ Configured' : '✗ Not set'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Push secret</p>
        </div>
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
          <p className={`text-sm font-bold mt-1 ${stats?.vapid_configured ? 'text-green-400' : 'text-red-400'}`}>
            {stats?.vapid_configured ? '✓ Ready' : '✗ Not set'}
          </p>
          <p className="text-xs text-gray-500 mt-1">VAPID keys</p>
        </div>
      </div>

      {/* Daily push */}
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-5">
        <h3 className="text-white text-sm uppercase tracking-wider mb-2">Daily Contextual Push</h3>
        <p className="text-gray-500 text-xs mb-4 leading-relaxed">
          Sends one personalised notification per subscriber who hasn't been notified in the last 20 hours.
          Message rotates based on ward data: unverified issues, new posts, Ward Pulse questions, council votes, points progress.
          <br/>
          <span className={`font-mono text-xs mt-1 inline-block ${reginaHour?.inWindow ? 'text-green-400' : 'text-red-400'}`}>
            {reginaHour
              ? `Regina time: ${String(reginaHour.hour).padStart(2,'0')}:${String(reginaHour.minute).padStart(2,'0')} · ${reginaHour.inWindow ? '✓ Within send window (8 AM – 8 PM)' : '✗ Outside send window — pushes blocked until 8 AM'}`
              : ''}
          </span>
        </p>
        <button onClick={sendDaily} disabled={sending}
          className="px-5 py-2 bg-[#C5A52A] hover:bg-yellow-400 text-black font-bold text-xs rounded disabled:opacity-50 transition-colors">
          {sending ? 'Sending…' : 'SEND DAILY NOTIFICATIONS'}
        </button>
      </div>

      {/* Emergency push */}
      <div className="bg-[#0a0a0a] border border-red-900/40 rounded p-5">
        <h3 className="text-red-400 text-sm uppercase tracking-wider mb-1">Emergency Broadcast</h3>
        <p className="text-gray-600 text-xs mb-4">
          Bypasses the 1/day limit. Reaches ALL subscribers immediately. Requires push secret.
          {reginaHour && !reginaHour.inWindow && (
            <span className="block text-yellow-500 mt-1">
              ⚠ Current Regina time is {String(reginaHour.hour).padStart(2,'0')}:{String(reginaHour.minute).padStart(2,'0')} — outside normal hours. Only use for genuine emergencies.
            </span>
          )}
        </p>
        <form onSubmit={sendEmergency} className="space-y-3">
          <input type="text" placeholder="Title" value={emergencyTitle}
            onChange={e => setEmergencyTitle(e.target.value)} required maxLength={80}
            className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded focus:border-red-600 focus:outline-none" />
          <textarea placeholder="Message" value={emergencyMsg}
            onChange={e => setEmergencyMsg(e.target.value)} required maxLength={200} rows={3}
            className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded resize-none focus:border-red-600 focus:outline-none" />
          <input type="url" placeholder="URL to open on tap (default: /)" value={emergencyUrl}
            onChange={e => setEmergencyUrl(e.target.value)}
            className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded focus:border-red-600 focus:outline-none" />
          <input type="password" placeholder="Push secret" value={pushSecret}
            onChange={e => setPushSecret(e.target.value)} required
            className="w-full bg-black border border-red-900/50 text-white text-sm px-3 py-2 rounded focus:border-red-600 focus:outline-none" />
          <button type="submit" disabled={emergencySending}
            className="px-5 py-2 bg-red-700 hover:bg-red-600 text-white font-bold text-xs rounded disabled:opacity-50 transition-colors">
            {emergencySending ? 'Sending…' : 'SEND EMERGENCY BROADCAST'}
          </button>
        </form>
      </div>

      {/* Set push secret */}
      <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded p-5">
        <h3 className="text-white text-sm uppercase tracking-wider mb-1">
          {stats?.push_secret_configured ? 'Change Push Secret' : 'Set Push Secret'}
        </h3>
        <p className="text-gray-600 text-xs mb-4">Required to send emergency pushes. Store it separately — it cannot be recovered.</p>
        <form onSubmit={saveSecret} className="space-y-3 max-w-sm">
          {stats?.push_secret_configured && (
            <input type="password" placeholder="Current push secret" value={currentSecret}
              onChange={e => setCurrentSecret(e.target.value)} required
              className="w-full bg-black border border-[#C5A52A]/40 text-white text-sm px-3 py-2 rounded focus:border-[#C5A52A] focus:outline-none" />
          )}
          <input type="password" placeholder="New push secret (min 8 chars)" value={newSecret}
            onChange={e => setNewSecret(e.target.value)} required minLength={8}
            className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded focus:border-[#C5A52A] focus:outline-none" />
          <input type="password" placeholder="Confirm new push secret" value={confirmSecret}
            onChange={e => setConfirmSecret(e.target.value)} required minLength={8}
            className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded focus:border-[#C5A52A] focus:outline-none" />
          <button type="submit" disabled={savingSecret}
            className="px-5 py-2 bg-zinc-700 hover:bg-zinc-600 text-white font-bold text-xs rounded disabled:opacity-50 transition-colors">
            {savingSecret ? 'Saving…' : stats?.push_secret_configured ? 'CHANGE SECRET' : 'SET SECRET'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PushTab;
